import Dropdown from 'antd/lib/dropdown';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import sparkleIcon from '../../../assets/sparkle.svg';
import React from 'react';
import { CopyOutlined, DownOutlined } from '@ant-design/icons';
import { useGetGeneratedReleaseNotes } from '@/components/useGetGeneratedReleaseNotes';
import { globalMessage } from '@/utils/globalMessage';
import { ReleaseComponentProps } from '@/typescriptHelpers';
import {
    GeneratedReleaseNotesMarkdownContext,
    GeneratedReleaseNotesStepContext,
    GeneratedReleaseNotesType,
} from '@/types';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import {
    selectAIDisabledForReleaseNotes,
    selectOrgSettings,
    selectShouldEnableExperimentalAIReleaseNotesFeature,
    selectUserIsRunwayAdmin,
} from '@/selectors/selectors';
import useUserBelongsToApp from '@/utils/hooks/useUserBelongsToApp';
import { MenuProps } from 'antd/lib/menu';

interface GenerateReleaseNotesDropdownProps extends ReleaseComponentProps {
    locale?: string;
    markdownContext: GeneratedReleaseNotesMarkdownContext;
    stepContext: GeneratedReleaseNotesStepContext;
    disallowedTypes?: GeneratedReleaseNotesType[];
}

export const GenerateReleaseNotesDropdown: React.FC<GenerateReleaseNotesDropdownProps> = ({
    orgId,
    appId,
    releaseId,
    stepContext,
    markdownContext,
    locale,
    disallowedTypes,
}) => {
    const isRunwayAdmin = useSelector((state: RootState) => selectUserIsRunwayAdmin(state));
    const userBelongsToApp = useUserBelongsToApp(appId);
    const isACLEnabled = useSelector((state: RootState) => selectOrgSettings(state).isACLEnabled);

    const shouldAllowAIGeneratedReleaseNotes = useSelector((state: RootState) => {
        const isAIFlagEnabled = selectShouldEnableExperimentalAIReleaseNotesFeature(state, orgId);
        const isAIDisabled = selectAIDisabledForReleaseNotes(state, orgId);
        return (isAIFlagEnabled || isRunwayAdmin) && !isAIDisabled;
    });

    const [generatedNotes, setGeneratedNotes] = React.useState('');
    const [isCopyButtonVisible, setIsCopyButtonVisible] = React.useState(false);
    const [isCopyButtonDisabled, setIsCopyButtonDisabled] = React.useState(true);

    const [getGeneratedReleaseNotes, isLoadingGetGeneratedReleaseNotes, getGeneratedReleaseNotesError] =
        useGetGeneratedReleaseNotes(orgId, appId, releaseId);

    const requestGeneratedNotes = async (
        notesType: GeneratedReleaseNotesType,
        stepContext: GeneratedReleaseNotesStepContext,
        markdownContext: GeneratedReleaseNotesMarkdownContext,
        locale: string,
    ) => {
        setGeneratedNotes('');
        setIsCopyButtonDisabled(true);

        const notes = await getGeneratedReleaseNotes(notesType, stepContext, markdownContext, locale);

        if (notes !== null && notes !== undefined) {
            globalMessage.success('Release notes were generated successfully and are ready to be copied to clipboard.');
            setGeneratedNotes(notes);
            setIsCopyButtonVisible(true);
            setIsCopyButtonDisabled(false);
        }
    };

    const handleClipboardWrite = async () => {
        navigator.clipboard
            .writeText(generatedNotes)
            .then(() => {
                globalMessage.success('Generated release notes copied to clipboard.');
            })
            .catch((e) => {
                globalMessage.error(
                    `Error copying generated release notes to clipboard: ${e}; please check your browser's clipboard permissions.`,
                );
            })
            .finally(() => {
                setGeneratedNotes('');
                setIsCopyButtonDisabled(true);
            });
    };

    React.useEffect(() => {
        if (getGeneratedReleaseNotesError) {
            globalMessage.error(`Failed to generate release notes: ${getGeneratedReleaseNotesError}`);
        }
    }, [getGeneratedReleaseNotesError]);

    const allNotesTypeItems: MenuProps['items'] = [
        {
            key: GeneratedReleaseNotesType.ReleaseNotesTypeAIGeneratedExternal,
            label: 'Generate with AI (for external audience)',
        },
        {
            key: GeneratedReleaseNotesType.ReleaseNotesTypeAIGeneratedInternal,
            label: 'Generate with AI (for internal audience)',
        },
        {
            key: GeneratedReleaseNotesType.ReleaseNotesTypeTicketFocus,
            label: 'Work items diff (ticket focus)',
        },
        {
            key: GeneratedReleaseNotesType.ReleaseNotesTypeCodeFocus,
            label: 'Work items diff (code focus)',
        },
        {
            key: GeneratedReleaseNotesType.ReleaseNotesTypeTemplated,
            label: 'Templated release summary',
        },
    ];
    const allowedNotesTypeItems = allNotesTypeItems.filter((item) => {
        const noteType = item.key as GeneratedReleaseNotesType;

        if (
            ((noteType == GeneratedReleaseNotesType.ReleaseNotesTypeAIGeneratedExternal ||
                noteType == GeneratedReleaseNotesType.ReleaseNotesTypeAIGeneratedInternal) &&
                !shouldAllowAIGeneratedReleaseNotes) ||
            (disallowedTypes && disallowedTypes.includes(noteType))
        ) {
            return false;
        } else {
            return true;
        }
    });

    const handleMenuClick = (e) => {
        const notesType: GeneratedReleaseNotesType = e.key;
        requestGeneratedNotes(notesType, stepContext, markdownContext, locale);
    };

    const menuWithProps = {
        items: allowedNotesTypeItems,
        onClick: handleMenuClick,
    };

    return (
        <Space size="small">
            <Dropdown
                menu={menuWithProps}
                trigger={['click']}
                disabled={isACLEnabled && !userBelongsToApp && !isRunwayAdmin}
            >
                <Button size="small" loading={isLoadingGetGeneratedReleaseNotes}>
                    <Space>
                        <img src={sparkleIcon} width={15} height={15} /> Auto-generate release notes <DownOutlined />
                    </Space>
                </Button>
            </Dropdown>
            {isCopyButtonVisible ?
                <Button size="small" disabled={isCopyButtonDisabled} type="link" onClick={handleClipboardWrite}>
                    <CopyOutlined /> Copy generated notes
                </Button>
            :   null}
        </Space>
    );
};
